// Pterodactyls patrol fixed circles (deterministic from server clock, so all
// clients see the same birds without any network traffic). Collision knocks
// the player's helicopter.
import {
  MeshBuilder, StandardMaterial, Color3, TransformNode, Vector3,
} from '@babylonjs/core';
import { PTEROS, terrainHeight, WATER_Y, DIVE_LEAD_MS } from '../shared/level.mjs';

function buildPteroMesh(scene) {
  const root = new TransformNode('ptero', scene);
  const m = new StandardMaterial('pteroMat', scene);
  m.diffuseColor = Color3.FromHexString('#8f6f4f');
  m.emissiveColor = m.diffuseColor.scale(0.15);
  m.specularColor = Color3.Black();
  const wingM = new StandardMaterial('pteroWing', scene);
  wingM.diffuseColor = Color3.FromHexString('#b08a5f');
  wingM.emissiveColor = wingM.diffuseColor.scale(0.15);
  wingM.backFaceCulling = false;

  const body = MeshBuilder.CreateCylinder('pbody', { height: 3.2, diameterTop: 0.2, diameterBottom: 0.9, tessellation: 6 }, scene);
  body.rotation.x = Math.PI / 2 + 0.1; body.material = m; body.parent = root;
  const head = MeshBuilder.CreateCylinder('phead', { height: 1.6, diameterTop: 0.05, diameterBottom: 0.5, tessellation: 5 }, scene);
  head.rotation.x = Math.PI / 2; head.position.set(0, 0.35, 2.1); head.material = m; head.parent = root;
  const crest = MeshBuilder.CreateCylinder('pcrest', { height: 1.1, diameterTop: 0.05, diameterBottom: 0.35, tessellation: 5 }, scene);
  crest.rotation.x = -Math.PI / 2 - 0.5; crest.position.set(0, 0.7, 1.4); crest.material = m; crest.parent = root;

  const wings = [];
  for (const s of [-1, 1]) {
    const pivot = new TransformNode('wingP', scene); pivot.parent = root; pivot.position.set(s * 0.3, 0.2, 0.3);
    const w = MeshBuilder.CreateGround('wing', { width: 3.6, height: 1.7 }, scene);
    w.material = wingM; w.parent = pivot;
    w.position.x = s * 1.8;
    wings.push({ pivot, s });
  }
  return { root, wings };
}

export function buildPteros(scene, defs = PTEROS) {
  return defs.map(def => ({
    def,
    mesh: buildPteroMesh(scene),
    lastHit: -10,
    down: false,        // knocked out of the sky by a stone
    groundPos: null,    // where it fell
    blend: 1,           // 0 = at groundPos … 1 = on patrol (for takeoff/fall)
    dive: null,         // { t0, from, to } — server-ordered attack run
  }));
}

const DIVE_SPEED = 34;    // m/s down the straight line
const DIVE_MS = 1.2;      // seconds of dive before it pulls out
const DIVE_BLEND = 2.5;   // seconds to fold back onto the analytic patrol

// Server-ordered attack run: the bird folds its wings and flies a STRAIGHT line
// from where it is toward (tx, ty, tz), then blends back onto its patrol circle.
// Because it always ends on the analytic path again, a lost 'dive' message
// self-heals within DIVE_BLEND seconds — no state can get stuck.
export function startDive(pteros, m) {
  const p = pteros.find(pp => pp.def.id === m.id);
  if (!p || p.down) return;
  p.dive = {
    t0: m.at != null ? m.at / 1000 : null,   // server seconds; null = start now
    // NOT snapshotted here: the server announces the dive DIVE_LEAD_MS early,
    // and the bird keeps patrolling until then — freezing `from` now would
    // teleport it back to where it was when the message was sent.
    from: null,
    to: new Vector3(m.tx, m.ty, m.tz),
  };
}

export function setPteroDown(pteros, id, isDown) {
  const p = pteros.find(pp => pp.def.id === id);
  if (!p || p.down === isDown) return;
  p.down = isDown;
  if (isDown) {
    // fall where it currently is
    const cur = p.mesh.root.position;
    const gy = Math.max(terrainHeight(cur.x, cur.z), WATER_Y) + 0.6;
    p.groundPos = new Vector3(cur.x, gy, cur.z);
    p.blend = 1; // will fall toward ground
  }
  // on 'up' the blend lerps back out from the ground position
}

// srvT: synced server time in seconds.
// Returns { v, at } on a hit: v = knock vector, at = where the bird was (so the
// caller can pan the screech). Null when nobody was hit.
export function updatePteros(pteros, srvT, heliPos, t, dt = 0.016) {
  let knock = null;
  for (const p of pteros) {
    const a = p.def.phase + p.def.w * srvT;
    const px = p.def.cx + Math.cos(a) * p.def.r;
    const pz = p.def.cz + Math.sin(a) * p.def.r;
    const py = p.def.alt + Math.sin(srvT * 0.7 + p.def.id * 3) * 6;

    // blend: 1 = on patrol, 0 = grounded where the stone dropped it
    const target = p.down ? 0 : 1;
    p.blend += (target - p.blend) * Math.min(1, dt * (p.down ? 2.8 : 1.2));

    if (p.groundPos && p.blend < 0.999) {
      p.mesh.root.position.set(
        p.groundPos.x + (px - p.groundPos.x) * p.blend,
        p.groundPos.y + (py - p.groundPos.y) * p.blend,
        p.groundPos.z + (pz - p.groundPos.z) * p.blend,
      );
    } else {
      p.mesh.root.position.set(px, py, pz);
      p.groundPos = null;
    }

    if (p.down && p.blend < 0.15) {
      // lying dazed: wings folded flat, slight wobble
      for (const w of p.mesh.wings) w.pivot.rotation.z = w.s * 1.25 + Math.sin(t * 2) * 0.05;
      p.mesh.root.rotation.z = 0.5;
      p.dive = null;
      continue; // no collision while grounded
    }

    const root = p.mesh.root;
    // face along the tangent of the circle
    const dir = p.def.w > 0 ? a + Math.PI / 2 : a - Math.PI / 2;
    root.rotation.y = Math.atan2(Math.cos(dir), -Math.sin(dir)) + Math.PI;
    // bank INTO the circle and pitch with the altitude sine it already follows
    root.rotation.z = -Math.sign(p.def.w) * 0.42 + Math.sin(t * 0.8 + p.def.id) * 0.06;
    root.rotation.x = -0.22 * Math.cos(srvT * 0.7 + p.def.id * 3);
    // fast downstroke, long glide — not a metronome
    const ph = (t * 0.9 + p.def.id) % 1;
    let flap = ph < 0.35 ? Math.sin(ph / 0.35 * Math.PI) * 0.75 : -0.12;

    if (p.dive) {
      if (p.dive.t0 === null) p.dive.t0 = srvT;
      const e = srvT - p.dive.t0;
      if (e > DIVE_MS + DIVE_BLEND || e < -(DIVE_LEAD_MS / 1000 + 1)) {
        p.dive = null;                        // finished, or a stale message
      } else if (e >= 0) {
        if (!p.dive.from) p.dive.from = root.position.clone();
        const from = p.dive.from, to = p.dive.to;
        const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
        const len = Math.max(0.001, Math.hypot(dx, dy, dz));
        const travel = Math.min(len, DIVE_SPEED * Math.min(e, DIVE_MS)) / len;
        const ax = from.x + dx * travel, ay = from.y + dy * travel, az = from.z + dz * travel;
        // after the run, ease back onto the analytic patrol position
        const u = e <= DIVE_MS ? 0 : Math.min(1, (e - DIVE_MS) / DIVE_BLEND);
        const k = u * u * (3 - 2 * u);        // 0 = full dive … 1 = back on patrol
        const fold = 1 - k;
        root.position.set(ax + (px - ax) * k, ay + (py - ay) * k, az + (pz - az) * k);
        // yaw down the dive line (same mirrored convention as the patrol yaw),
        // blended the short way round so it never spins through 360°
        const diveYaw = -Math.atan2(dx, dz);
        let dyaw = (root.rotation.y - diveYaw + Math.PI) % (Math.PI * 2);
        if (dyaw < 0) dyaw += Math.PI * 2;
        root.rotation.y = diveYaw + (dyaw - Math.PI) * k;
        root.rotation.z = -Math.sign(p.def.w) * 0.5 * fold + root.rotation.z * k;
        root.rotation.x = -Math.atan2(dy, Math.hypot(dx, dz)) * fold + root.rotation.x * k;
        flap = flap * k + 1.4 * fold;         // wings folded back for the stoop
      }
    }
    for (const w of p.mesh.wings) w.pivot.rotation.z = w.s * flap;

    if (heliPos && !p.down && t - p.lastHit > 3) {
      const d = Vector3.Distance(root.position, heliPos);
      // a stooping bird has a slightly fatter hitbox — it is aiming at you
      if (d < (p.dive ? 6.0 : 5.5)) {
        p.lastHit = t;
        const away = heliPos.subtract(root.position).normalize();
        knock = {
          v: away.scale(16).add(new Vector3(0, -6, 0)),
          at: root.position.clone(),
          diving: !!p.dive,
        };
      }
    }
  }
  return knock;
}
